import axios from 'axios';

interface NewsletterSubscription {
  email: string;
  firstName?: string;
  lastName?: string;
  source?: 'blog' | 'homepage' | 'footer' | 'popup' | 'manual';
  preferences?: {
    blogUpdates: boolean;
    caseStudies: boolean;
    industryNews: boolean;
    weeklyDigest: boolean;
  };
}

interface SubscriptionResponse {
  success: boolean;
  message: string;
  data?: {
    email: string;
    status: string;
    subscribedAt?: string;
    unsubscribedAt?: string;
  };
}

interface SubscriptionStatus {
  subscribed: boolean;
  status: string;
  subscribedAt?: string;
  preferences?: {
    blogUpdates: boolean;
    caseStudies: boolean;
    industryNews: boolean;
    weeklyDigest: boolean;
  };
  unsubscribeToken?: string;
}

class NewsletterService {
  private baseURL = '/api/newsletter';

  /**
   * Subscribe to newsletter
   */
  async subscribe(subscriptionData: NewsletterSubscription): Promise<SubscriptionResponse> {
    try {
      const response = await axios.post(`${this.baseURL}/subscribe`, subscriptionData);
      return response.data;
    } catch (error: any) {
      console.error('Error subscribing to newsletter:', error);
      throw new Error(error.response?.data?.message || 'Failed to subscribe to newsletter');
    }
  }

  /**
   * Unsubscribe from newsletter
   */
  async unsubscribe(email: string, token?: string): Promise<SubscriptionResponse> {
    try {
      const response = await axios.post(`${this.baseURL}/unsubscribe`, { email, token });
      return response.data;
    } catch (error: any) {
      console.error('Error unsubscribing from newsletter:', error);
      throw new Error(error.response?.data?.message || 'Failed to unsubscribe from newsletter');
    }
  }

  /**
   * Check subscription status
   */
  async getSubscriptionStatus(email: string): Promise<SubscriptionStatus> {
    try {
      const response = await axios.get(`${this.baseURL}/status/${encodeURIComponent(email)}`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error checking subscription status:', error);
      throw new Error(error.response?.data?.message || 'Failed to check subscription status');
    }
  }

  /**
   * Validate email format
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Format subscription data
   */
  formatSubscriptionData(data: Partial<NewsletterSubscription>): NewsletterSubscription {
    return {
      email: data.email?.toLowerCase().trim() || '',
      firstName: data.firstName?.trim() || undefined,
      lastName: data.lastName?.trim() || undefined,
      source: data.source || 'blog',
      preferences: data.preferences || {
        blogUpdates: true,
        caseStudies: true,
        industryNews: true,
        weeklyDigest: true
      }
    };
  }

  /**
   * Generate unsubscribe URL
   */
  generateUnsubscribeURL(email: string, token: string): string {
    return `${window.location.origin}/unsubscribe?email=${encodeURIComponent(email)}&token=${token}`;
  }
}

export default new NewsletterService();
export type { NewsletterSubscription, SubscriptionResponse, SubscriptionStatus };
